import { useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../../api/client'
import type { AssetTag } from '../../api/types'
import { Card } from '../../components/ui'
import { describeWriteFailure } from './messages'
import { DOWNLOADS, detectOs, detectWriter, type Os } from './platform'
import type { NfcPayload, NfcWriter, NfcWriterStatus } from './types'

type Phase = NfcWriterStatus | { state: 'checking' } | { state: 'not_installed' }

const DEFAULT_POLL_MS = 2000

/** Probes the writer on mount and every `pollMs` while the page is visible; stops on unmount. Paused during a write. */
function useWriterStatus(writer: NfcWriter, pollMs: number, paused: boolean): Phase {
  const [phase, setPhase] = useState<Phase>({ state: 'checking' })
  const pausedRef = useRef(paused)
  useEffect(() => { pausedRef.current = paused }, [paused])

  useEffect(() => {
    const controller = new AbortController()
    let timer: ReturnType<typeof setTimeout> | undefined
    let inFlight = false
    const tick = async () => {
      if (controller.signal.aborted || inFlight) return
      if (document.visibilityState === 'visible' && !pausedRef.current) {
        inFlight = true
        try {
          const status = await writer.getStatus(controller.signal)
          if (!controller.signal.aborted) setPhase(status)
        } catch {
          if (!controller.signal.aborted) setPhase({ state: 'not_installed' })
        } finally {
          inFlight = false
        }
      }
      if (!controller.signal.aborted) timer = setTimeout(() => void tick(), pollMs)
    }
    const onVisibility = () => {
      if (document.visibilityState === 'visible') { clearTimeout(timer); void tick() }
    }
    document.addEventListener('visibilitychange', onVisibility)
    void tick()
    return () => {
      controller.abort()
      clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [writer, pollMs])

  return phase
}

const DESKTOP_STEPS = [
  'Descargar el instalador e instalar el programa «Motores NFC».',
  'Abrir el programa; queda activo en segundo plano.',
  'Conectar el lector NFC por USB.',
  'Volver a esta página: la conexión se detecta sola en unos segundos.',
]
const ANDROID_STEPS = [
  'Descargar la app de campo e instalarla (el sistema puede pedir permiso para instalar desde esta fuente).',
  'Abrir la app e iniciar sesión.',
  'Activar el NFC en los ajustes del teléfono.',
  'Abrir esta misma sección desde la app de campo.',
]

function InstallHelp({ os }: { os: Os }) {
  const links: { href: string; label: string }[] =
    os === 'windows' ? [{ href: DOWNLOADS.windows, label: 'Descargar instalador' }]
      : os === 'linux' ? [{ href: DOWNLOADS.linux, label: 'Descargar instalador' }]
        : os === 'android' ? [{ href: DOWNLOADS.android, label: 'Descargar app de campo' }]
          : [{ href: DOWNLOADS.windows, label: 'Descargar instalador (Windows)' }, { href: DOWNLOADS.linux, label: 'Descargar instalador (Linux)' }]
  const steps = os === 'android' ? ANDROID_STEPS : DESKTOP_STEPS
  return (
    <>
      <p>{os === 'android' ? 'No se detecta la app de campo. Para escribir etiquetas NFC se necesita la app de campo.' : 'No se detecta el programa NFC en este equipo. Para escribir etiquetas se necesita instalarlo.'}</p>
      <div className="row">
        {links.map((l) => <a key={l.href} className="btn btn--primary btn--sm" href={l.href} download>{l.label}</a>)}
      </div>
      <ol aria-label="Pasos de instalación" className="small">
        {steps.map((s) => <li key={s}>{s}</li>)}
      </ol>
    </>
  )
}

function StatusBody({ phase, os }: { phase: Phase; os: Os }) {
  switch (phase.state) {
    case 'checking':
      return <p className="muted">Buscando el programa o la app NFC…</p>
    case 'not_installed':
      return <InstallHelp os={os} />
    case 'no_reader':
      return <><strong>Conectar el lector NFC</strong><p className="muted">El programa NFC está activo, pero no hay ningún lector conectado. Conectarlo por USB; se detecta solo.</p></>
    case 'no_tag':
      return <><strong>Acercar una etiqueta</strong><p className="muted">Colocar la etiqueta sobre el lector. El botón se habilita al detectarla.</p></>
    case 'tag_present':
      return <><strong>Etiqueta detectada{phase.uid ? `. UID ${phase.uid}` : ''}</strong>{phase.protectedTag ? <p className="muted">La etiqueta ya está protegida; se reescribirá solo si fue protegida desde esta aplicación.</p> : null}</>
    case 'ready':
      return <><strong>Listo para escribir</strong><p className="muted">Al pulsar el botón, acercar el teléfono a la etiqueta y mantenerlo quieto.</p></>
    case 'no_nfc':
      return <p>Este dispositivo no tiene NFC. Usar un teléfono con NFC o el lector de escritorio.</p>
    case 'nfc_disabled':
      return <p>El NFC está desactivado. Activarlo en los ajustes del dispositivo; se detecta solo.</p>
  }
}

/** The derived password only exists inside this call: never stored in state, logged or returned. */
async function fetchAndWrite(tagId: number, writer: NfcWriter) {
  const payload = await api.post<NfcPayload>(`/api/asset-tags/${tagId}/nfc-payload/`)
  return writer.write(payload)
}

type Result = { ok: true; uid: string; alreadyProtected: boolean } | { ok: false; message: string }

interface Props {
  /** Registered tags of the equipment; only active NFC tags can be written. */
  tags: AssetTag[]
  /** Injected in tests; by default the Android plugin inside the shell, otherwise the desktop bridge. */
  writer?: NfcWriter
  userAgent?: string
  pollMs?: number
}

export function NfcWritePanel({ tags, writer, userAgent, pollMs = DEFAULT_POLL_MS }: Props) {
  const active = useMemo(() => writer ?? detectWriter(), [writer])
  const os = detectOs(userAgent ?? navigator.userAgent)
  const eligible = tags.filter((t) => t.kind === 'nfc' && t.active && !t.revoked_at)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<Result | null>(null)
  const phase = useWriterStatus(active, pollMs, busy)
  const chosen = eligible.find((t) => t.id === selectedId) ?? eligible[0]
  const canWrite = !!chosen && !busy && (phase.state === 'tag_present' || phase.state === 'ready')

  async function onWrite() {
    if (!chosen) return
    setBusy(true)
    setResult(null)
    try {
      const written = await fetchAndWrite(chosen.id, active)
      setResult({ ok: true, uid: written.uid, alreadyProtected: written.alreadyProtected })
    } catch (error) {
      setResult({ ok: false, message: describeWriteFailure(error) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title="Escribir etiqueta NFC">
      <div role="status" className="nfc-status">
        <StatusBody phase={phase} os={os} />
      </div>
      {eligible.length === 0 ? (
        <p className="muted">No hay etiquetas NFC activas para escribir. Registrar una etiqueta NFC primero.</p>
      ) : (
        <div className="row">
          {eligible.length > 1 ? (
            <label>Etiqueta a escribir{' '}
              <select value={chosen?.id} onChange={(e) => setSelectedId(Number(e.target.value))}>
                {eligible.map((t) => <option key={t.id} value={t.id}>NFC {t.uid ?? 'sin UID'} (n.º {t.id})</option>)}
              </select>
            </label>
          ) : null}
          <button type="button" className="btn btn--primary btn--sm" disabled={!canWrite} onClick={() => void onWrite()}>
            {busy ? 'Escribiendo…' : 'Escribir y proteger etiqueta'}
          </button>
        </div>
      )}
      {result?.ok ? (
        <div role="status" className="nfc-result">
          <p>{`Etiqueta escrita y protegida. UID ${result.uid}`}</p>
          {result.alreadyProtected ? <p className="muted">La etiqueta ya estaba protegida; se reescribió con la contraseña de la aplicación.</p> : null}
        </div>
      ) : null}
      {result && !result.ok ? <p role="alert" className="error-text">{result.message}</p> : null}
      <p className="faint small">La etiqueta queda protegida con contraseña: solo se puede reescribir desde esta aplicación.</p>
    </Card>
  )
}
